import { useEffect, useState } from "react";
import MonitorService from "../../services/monitor.service";
import { useNavigate, useParams } from "react-router-dom";
import { Condition } from "../../types/Condition";
import { XMarkIcon } from "@heroicons/react/24/outline";
import QueryBuilder, { Field, formatQuery, ValueEditorProps } from "react-querybuilder";
import { parseJsonLogic } from 'react-querybuilder/parseJsonLogic';
import Breadcrumb from "../common/breadcrumb/breadcrumb";
import { useTranslation } from "react-i18next";
import 'react-querybuilder/dist/query-builder.css';
import './condition.styles.css';
import Dropdown from "../common/dropdown";
import { DropdownOption } from "../../models/DropdownOption";
import { toastSuccess } from "../../App";

const valueEditor = ({ handleOnChange, value, operator }: ValueEditorProps) => {
  if (operator === 'between' || operator === 'notBetween') {
    const [start, end] = value.split(',');

    return (
      <div className="flex space-x-2">
        <input
          type="text"
          value={start}
          className="rounded-md border border-gray-300 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
          onChange={e => handleOnChange(`${e.target.value},${end || ''}`)}
        />
        <input
          type="text"
          value={end}
          className="rounded-md border border-gray-300 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
          onChange={e => handleOnChange(`${start || ''},${e.target.value}`)}
        />
      </div>
    );
  } else if (operator == 'null' || operator == 'notNull') {
    value = null;
    return <div></div>;
  } else {
    return (
      <input
        type="text"
        value={value}
        className="rounded-md border border-gray-300 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
        onChange={(e) => handleOnChange(e.target.value)}
      />
    );
  }
};

export default function ConditionPage() {
  const navigate = useNavigate();
  const { monitorId, conditionId } = useParams();
  const [condition, setCondition] = useState<Condition>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<DropdownOption<number>>();
  const [muted, setMuted] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const canSave = name.trim() !== '' && description.trim() !== '' && priority !== undefined;
  const [query, setQuery] = useState<any>({ id: '0', rules: [] });

  const { t } = useTranslation();

  const priorities: DropdownOption<number>[] = [
    { value: 1, label: 'P1' },
    { value: 2, label: 'P2' },
    { value: 3, label: 'P3' },
    { value: 4, label: 'P4' },
    { value: 5, label: 'P5' },
  ];

  const fields: Field[] = [
    { name: "l1Arms", label: t("monitors.compressorAmps"), inputType: "number" },
    { name: "l2Arms", label: t("monitors.fanAmps"), inputType: "number" },
    { name: "t1F", label: t("monitors.suctionTemp"), inputType: "number" },
    { name: "t2F", label: t("monitors.dischargeTemp"), inputType: "number" },
    { name: "t3F", label: t("monitors.liquidLineTemp"), inputType: "number" },
    { name: "p1Psi", label: t("monitors.suctionPressure"), inputType: "number" },
    { name: "p2Psi", label: t("monitors.liquidLinePressure"), inputType: "number" },
    { name: "subcooling", label: t("monitors.subcooling"), inputType: "number" },
    { name: "superheat", label: t("monitors.superheat"), inputType: "number" },
    { name: "deltaP", label: t("monitors.deltaP"), inputType: "number" }
  ];

  const breadcrumbPages = [
    { name: 'Monitors List', href: '/monitors', current: false, isHome: true },
    { name: t('monitors.monitorDetail'), href: `/monitors/${monitorId}`, current: false },
    { name: t('monitors.conditionDetail'), href: `/monitors/${monitorId}/conditions/${conditionId}`, current: true },
  ];

  const fetchData = async () => {
    if (monitorId && conditionId) {
      const response = await MonitorService.getCondition(monitorId, conditionId);
      const data = await response.json();
      data.logic = parseJsonLogic(data.logic);
      setQueryIds(data.logic);

      setCondition(data);
      setName(data.name);
      setDescription(data.description);
      setPriority(priorities.find((option) => option.value === data.priority),);
      setMuted(data.muted);
      setDisabled(data.disabled);
      setQuery(data.logic);
    }
  };

  const setQueryIds = (logic: any) => {
    logic.rules.map((rule: any, i: number) => {
      rule.id = i.toString();

      if (rule.rules) {
        setQueryIds(rule);
      }
    });
  }

  const handleQueryChange = (query: any) => {
    setQuery(query);
  }

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = () => {
    if (monitorId) {
      if (conditionId) {
        const updatedCondition: any = {
          ...(condition?.name !== name && { name }),
          ...(condition?.description !== description && { description }),
          ...(condition?.priority !== priority?.value && { priority: priority?.value }),
          ...(condition?.muted !== muted && { muted }),
          ...(condition?.disabled !== disabled && { disabled }),
          ...(condition?.logic !== query && { logic: formatQuery(query, 'jsonlogic') }),
        }

        MonitorService.updateCondition(monitorId, conditionId, updatedCondition).then(response => {
          response.json().then(data => {
            setCondition(data);
            toastSuccess(t('monitors.conditionUpdated'));
          });
        });
      } else {
        const newCondition = {
          priority: priority?.value,
          name,
          muted,
          disabled,
          description,
          logic: formatQuery(query, 'jsonlogic'),
        };

        if (monitorId) {
          MonitorService.addCondition(monitorId, newCondition).then(response =>
            response.json().then(data => {
              setCondition(data);
              toastSuccess(t('monitors.conditionAdded'));
              navigate(`/monitors/${monitorId}/conditions/${data._id}`);
            })
          );
        }
      }
    }
  }

  const operators = [
    { operator: '=', label: '=' },
    { operator: '!=', label: '!=' },
    { operator: '<', label: '<' },
    { operator: '>', label: '>' },
    { operator: '<=', label: '<=' },
    { operator: '>=', label: '>=' },
    { operator: 'contains', label: t('monitors.contains') },
    { operator: 'beginsWith', label: t('monitors.beginsWith') },
    { operator: 'endsWith', label: t('monitors.endsWith') },
    { operator: 'doesNotContain', label: t('monitors.doesNotContain') },
    { operator: 'doesNotBeginWith', label: t('monitors.doesNotBeginWith') },
    { operator: 'doesNotEndWith', label: t('monitors.doesNotEndWith') },
    { operator: 'null', label: t('monitors.isNull') },
    { operator: 'notNull', label: t('monitors.isNotNull') },
    { operator: 'in', label: t('monitors.in') },
    { operator: 'notIn', label: t('monitors.notIn') },
    { operator: 'between', label: t('monitors.between') },
    { operator: 'notBetween', label: t('monitors.notBetween') },
  ];

  return (
    <>
      <div className={`pt-3 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8`}>
        <Breadcrumb pages={breadcrumbPages}></Breadcrumb>

        <div className="flex flex-row items-center justify-between pt-3">
          <div>
            <h1 className="text-lg font-bold leading-6 text-gray-900">
              {t('monitors.condition')}
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              {t('monitors.conditionDesc')}
            </p>
          </div>

          <button
            type="button"
            className={`rounded-md border border-transparent ${canSave ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-200'
              } py-2 px-3 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm`}
            onClick={handleSave}
            disabled={!canSave}
          >
            {t('save')}
          </button>
        </div>

        <div className="mt-6 grid grid-cols-6 gap-6">
          <div className="col-span-4 sm:col-span-2">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700"
            >
              {t('monitors.name')}
            </label>

            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
            />
          </div>

          <div className="col-span-8 sm:col-span-4">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700"
            >
              {t('monitors.description')}
            </label>

            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
            />
          </div>

          <div className="flex flex-row col-span-12 sm:col-span-6 mb-6 grid grid-cols-6">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700">
                {t('monitors.priority')}
              </label>

              <Dropdown
                options={priorities}
                selectedOption={priority}
                onChange={setPriority}
              />
            </div>

            <div className="ml-auto col-start-5 col-span-1">
              <label
                htmlFor="muted"
                className="block text-sm font-medium text-gray-700"
              >
                {t('monitors.muted')}
              </label>

              <input
                type="checkbox"
                checked={muted}
                onChange={() => setMuted(!muted)}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 w-5 h-5 rounded-md border border-gray-300"
              />
            </div>

            <div className="ml-auto col-span-1">
              <label
                htmlFor="muted"
                className="block text-sm font-medium text-gray-700"
              >
                {t('monitors.disabled')}
              </label>

              <input
                type="checkbox"
                checked={disabled}
                onChange={() => setDisabled(!disabled)}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 w-5 h-5 rounded-md border border-gray-300"
              />
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div>
            <h3 className="text-lg font-bold leading-6 text-gray-900">
              {t('monitors.alertLogic')}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {t('monitors.logicDesc')}
            </p>
          </div>

          <QueryBuilder
            query={query}
            onQueryChange={(newQuery) => handleQueryChange(newQuery)}
            fields={fields}
            controlElements={{
              combinatorSelector: ({ handleOnChange }) => (
                <select
                  className="rounded-md border border-gray-300 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                  onChange={handleOnChange}
                >
                  <option value="and">{t('monitors.and')}</option>
                  <option value="or">{t('monitors.or')}</option>
                </select>
              ),
              addRuleAction: ({ handleOnClick }) => (
                <button
                  className="flex items-center rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                  onClick={handleOnClick}
                >
                  {t('monitors.addRule')}
                </button>
              ),
              addGroupAction: ({ handleOnClick }) => (
                <button
                  className="flex items-center rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                  onClick={handleOnClick}
                >
                  {t('monitors.addGroup')}
                </button>
              ),
              removeRuleAction: ({ handleOnClick }) => (
                <button
                  className="rounded-md p-1  border-transparent bg-red-600 text-white shadow-sm hover:bg-red-700"
                  onClick={handleOnClick}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              ),
              removeGroupAction: ({ handleOnClick }) => (
                <button
                  className="rounded-md p-1  border-transparent bg-red-600 text-white shadow-sm hover:bg-red-700"
                  onClick={handleOnClick}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              ),
              operatorSelector: ({ value, handleOnChange }) => (
                <select
                  value={value}
                  className="rounded-md border border-gray-300 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
                  onChange={(e) => handleOnChange(e.target.value)}
                >
                  {operators.map((operator, i) => (
                    <option key={i} value={operator.operator}>
                      {operator.label}
                    </option>
                  ))}
                </select>
              ),
              valueEditor: valueEditor,
            }}
            controlClassnames={{
              queryBuilder:
                'queryBuilder-branches mt-1 rounded-md bg-gray-100',
              header: 'flex gap-x-4',
              body: 'grid gap-4',
              rule: 'flex gap-x-4',
              fields: 'rounded-md border border-gray-300 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm',
              operators: 'rounded-md border border-gray-300 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm',
              value: 'rounded-md border border-gray-300 py-2 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm',
            }}
          />
        </div>
      </div>
    </>
  );
}
